import { Checkbox } from '@mui/material';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { CatalogItemSource } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import { fetchOrder } from 'entities/Order';
import { getSelectedOrderId } from 'entities/Order/model/selectors/ordersSelectors';
import { getUserCanEditPropertyCatalog } from 'entities/User/model/selectors/userPermissionsSeletors';
import { AddCatalogItemWithQty } from 'features/AddCatalogItemWithQty';
import { addItemsToOrderService } from 'features/AddItemsToOrder/model/services/addItemsToOrderService';
import { addItemsToOrderActions } from 'features/AddItemsToOrder/model/slices/addItemsToOrderSlice';
import { editOrderItem } from 'features/editItemsInOrder/model/services/editOrderItem/editOrderItem';
import { EditItemsInOrderRequestParams } from 'features/editItemsInOrder/model/types/editItemsInOrderSchema';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { getRouteNotFound } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useDebouncedCallback } from 'shared/lib/hooks/useDebouncedCallback';

interface CatalogItemCheckboxProps {
    isActive: boolean;
    showQtyCheckbox: boolean;
    quickEdit: boolean;
    isInOrder: boolean;
    qty: number;
    itemId: string;
    // TODO: tmp solution until catalog item IDs are unified across order and catalog contexts
    originalItemId: string;
    itemUid: string;
    itemIdToEdit: string;
    isOrderDetails?: boolean;
    disabled?: boolean;
    isAvailable?: boolean;
    source?: CatalogItemSource;
}

enum OrderActionType {
    ADD_ITEM = 'add',
    DELETE_ITEM = 'delete',
    EDIT_QTY_ITEM = 'edit',
}

export const CatalogItemCheckbox = ({
    isActive,
    showQtyCheckbox,
    itemId,
    originalItemId,
    itemUid,
    itemIdToEdit,
    quickEdit,
    isInOrder,
    qty,
    isOrderDetails,
    disabled = false,
    isAvailable = true,
    source,
}: CatalogItemCheckboxProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const canEditCatalog = useSelector(getUserCanEditPropertyCatalog);
    const selectedOrderId = useSelector(getSelectedOrderId);

    const [loading, setLoading] = useState(false);

    const redirectOnNotFound = useCallback(
        () => navigate(getRouteNotFound()),
        [navigate],
    );

    const updateOrder = useDebouncedCallback(
        async (
            params: EditItemsInOrderRequestParams,
            action: OrderActionType,
        ) => {
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(true));
            setLoading(true);
            switch (action) {
                case OrderActionType.ADD_ITEM:
                    await dispatch(
                        addItemsToOrderService({
                            data: {
                                ...(source === 'marketplace'
                                    ? {
                                          globalItems: [
                                              {
                                                  itemUuid: params.itemId,
                                                  qty: params.qty,
                                              },
                                          ],
                                      }
                                    : {
                                          items: [
                                              {
                                                  itemId: params.itemId,
                                                  qty: params.qty,
                                              },
                                          ],
                                      }),
                            },
                            orderId: params.orderId,
                        }),
                    );
                    break;

                case OrderActionType.DELETE_ITEM:
                    await dispatch(deleteOrderItem(params));
                    break;

                case OrderActionType.EDIT_QTY_ITEM:
                    await dispatch(editOrderItem(params));
                    break;
                default:
                    break;
            }
            isOrderDetails &&
                (await dispatch(
                    fetchOrder({
                        id: selectedOrderId,
                        onNotFound: redirectOnNotFound,
                    }),
                ));
            dispatch(addItemsToOrderActions.setIsLoadingAddItemsToOrder(false));
            setLoading(false);
        },
    );

    const onItemToggleSelect = useCallback(
        (id: string) => async () => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: itemIdToEdit,
                qty,
                ...(quickEdit && { source }),
            };

            if ((!qty || qty === 0) && !isInOrder) {
                dispatch(
                    catalogItemActions.addMultiselectItem({
                        id,
                        uid: itemUid,
                    }),
                );

                if (quickEdit) {
                    dispatch(
                        catalogItemActions.addNewItem({
                            itemId: id,
                            source,
                        }),
                    );
                    selectedOrderId &&
                        (await updateOrder(
                            { ...dataToEdit, qty: 1 },
                            OrderActionType.ADD_ITEM,
                        ));
                }
            } else {
                dispatch(catalogItemActions.removeMultiselectItem(id));
                if (quickEdit) {
                    selectedOrderId &&
                        (await updateOrder(
                            { ...dataToEdit, qty: 0 },
                            OrderActionType.DELETE_ITEM,
                        ));

                    if (id === itemIdToEdit) {
                        dispatch(catalogItemActions.removeItem(id));
                    }
                }
            }
        },
        [
            selectedOrderId,
            itemIdToEdit,
            qty,
            isInOrder,
            dispatch,
            itemUid,
            source,
            quickEdit,
            updateOrder,
        ],
    );

    const onSetItemValue = useCallback(
        (id: string) => async (qty: number) => {
            const dataToEdit = {
                orderId: selectedOrderId,
                itemId: itemIdToEdit,
                qty,
                ...(quickEdit && { source }),
            };

            if (qty < 1) {
                if (quickEdit && id === itemIdToEdit) {
                    dispatch(catalogItemActions.removeItem(id));
                } else {
                    dispatch(catalogItemActions.toggleMultiselectItem(id));
                }
            } else {
                dispatch(
                    catalogItemActions.setItemQuantity({ id, value: qty }),
                );
            }
            if (quickEdit) {
                dispatch(
                    addItemsToOrderActions.setIsLoadingAddItemsToOrder(true),
                );
                if (qty >= 1 && qty) {
                    selectedOrderId &&
                        (await updateOrder(
                            dataToEdit,
                            OrderActionType.EDIT_QTY_ITEM,
                        ));
                } else if (qty <= 0) {
                    dispatch(catalogItemActions.removeMultiselectItem(id));
                    dispatch(catalogItemActions.removeNewItem(id));
                    selectedOrderId &&
                        (await updateOrder(
                            dataToEdit,
                            OrderActionType.DELETE_ITEM,
                        ));
                }

                dispatch(
                    addItemsToOrderActions.setIsLoadingAddItemsToOrder(false),
                );
            }
        },
        [
            selectedOrderId,
            itemIdToEdit,
            quickEdit,
            source,
            dispatch,
            updateOrder,
        ],
    );

    return !showQtyCheckbox ? (
        <Checkbox
            disableRipple
            disabled={disabled}
            sx={{
                display: canEditCatalog ? 'inline-flex' : 'none',
                paddingTop: '0px',
                '& >.MuiSvgIcon-root': {
                    width: '28px',
                    height: '28px',
                },
            }}
            checked={isActive}
            onClick={disabled ? undefined : onItemToggleSelect(itemIdToEdit)}
        />
    ) : (
        <AddCatalogItemWithQty
            value={qty}
            active={isActive}
            onItemToggleSelect={onItemToggleSelect(originalItemId)}
            onSetValue={onSetItemValue(itemId)}
            loading={loading}
            disabledAddButton={!isAvailable}
        />
    );
};
